import { MarketButton } from '@market/react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { withI18n } from '@squareup/service-i18n/components';
import { BankingDialog } from '../../ui/modal/BankingDialog';

const CONTACT_SUPPORT_URL = 'https://app.squareup.com/help/us/en/contact?panel=236A03B17CB1';

export interface CancelCreditCardPaymentModalProps {
  readonly active: boolean;
  readonly amount?: string;
  readonly bankAccountSuffix?: string;
  readonly isAccountEligible?: boolean;
  readonly isCanceling?: boolean;
  readonly onGoBack: () => void;
  readonly onCancelPayment?: () => void;
}

export const CancelCreditCardPaymentModal = ({
  active,
  amount,
  bankAccountSuffix,
  isAccountEligible = true,
  isCanceling = false,
  onGoBack,
  onCancelPayment,
}: CancelCreditCardPaymentModalProps): JSX.Element | null => {
  const { t } = useTranslation();

  if (!isAccountEligible) {
    const ineligibleButtons = [
      <MarketButton key="go-back" rank="primary" onClick={onGoBack}>
        {t('bankingShared.creditCard.cancelPaymentModal.goBack')}
      </MarketButton>,
      <MarketButton
        key="contact-support"
        rank="secondary"
        href={CONTACT_SUPPORT_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        {t('bankingShared.creditCard.cancelPaymentModal.contactSupport')}
      </MarketButton>,
    ];

    return (
      <BankingDialog
        active={active}
        buttons={ineligibleButtons}
        content={t('bankingShared.creditCard.cancelPaymentModal.ineligibleDescription')}
        title={t('bankingShared.creditCard.cancelPaymentModal.ineligibleTitle')}
        onModalClose={onGoBack}
      />
    );
  }

  const buttons = [
    <MarketButton
      key="cancel-payment"
      rank="primary"
      isLoading={isCanceling}
      onClick={onCancelPayment}
    >
      {t('bankingShared.creditCard.cancelPaymentModal.cancelPayment')}
    </MarketButton>,
    <MarketButton key="go-back" rank="secondary" onClick={onGoBack}>
      {t('bankingShared.creditCard.cancelPaymentModal.goBack')}
    </MarketButton>,
  ];

  const content = (
    <>
      <p className="paragraph-30">
        {t('bankingShared.creditCard.cancelPaymentModal.description', { amount, bankAccountSuffix })}
      </p>
      <p className="paragraph-30">
        {t('bankingShared.creditCard.cancelPaymentModal.balanceIncrease', { amount })}
      </p>
    </>
  );

  return (
    <BankingDialog
      active={active}
      buttons={buttons}
      content={content}
      title={t('bankingShared.creditCard.cancelPaymentModal.title')}
      onModalClose={onGoBack}
    />
  );
};

export const CancelCreditCardPaymentModalI18n = withI18n(CancelCreditCardPaymentModal);
